import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
    Button,
    CircularProgress,
    Grid,
    Typography,
    makeStyles,
} from '@material-ui/core';
import MigaPan from '../../../app/orussystem/component/MigaPan';
import Title from '../../Imc/Title';
import { getPreinformeByDocumento, getInformeByNumero } from '../../domain/api/PreinformeApi';
import {
    extractPreinforme,
    resolvePersonalData,
    extractInformeUrl,
    parsePreinformePayload,
    buildTarjetasValor,
    buildTarjetasMediciones,
    labelIndicador,
    normalizarListaNarrativa,
    resolveMetas12Semanas,
    normalizarAdvertencias,
} from '../utils/HelpPreinforme';
import { UtilsDate } from '../../../app/orussystem/utils/UtilDate';
import CabeceraPreinforme from './preinforme/CabeceraPreinforme';
import MetricaCard from './preinforme/MetricaCard';
import ValorCard from './preinforme/ValorCard';
import SeccionNarrativa from './preinforme/SeccionNarrativa';
import MetasPanel from './preinforme/MetasPanel';
import AvisoAdvertencias from './preinforme/AvisoAdvertencias';
import preinformeStyles from './preinforme/preinformeStyles';

const useStyles = makeStyles((theme) => ({
    loadingBox: {
        display: 'flex',
        alignItems: 'center',
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(2),
    },
    loadingText: {
        marginLeft: theme.spacing(2),
    },
    volverStandalone: {
        textTransform: 'none',
        marginTop: theme.spacing(2.5),
    },
}));

const PreinformeUser = () => {
    const { documento, idBascula, nameBascula } = useParams();
    const history = useHistory();
    const location = useLocation();
    const dispatch = useDispatch();
    const classes = useStyles();
    const classesPre = preinformeStyles();

    const { busqueda } = location.state || {};
    const personalData = resolvePersonalData(location.state, { documento });

    const [isLoad, setIsLoad] = useState(false);
    const [isError, setIsError] = useState(false);
    const [preinforme, setPreinforme] = useState(null);

    const [isInformeLoad, setIsInformeLoad] = useState(false);
    const [isInformeError, setIsInformeError] = useState(false);
    const [informeUrl, setInformeUrl] = useState(null);

    const fetchPreinforme = useCallback(async () => {
        setIsError(false);
        setIsLoad(true);
        const response = await getPreinformeByDocumento(dispatch, documento);
        if (!response || response.error === true) {
            setIsError(true);
        } else {
            setPreinforme(extractPreinforme(response));
        }
        setIsLoad(false);
    }, [dispatch, documento]);

    useEffect(() => {
        fetchPreinforme();
    }, [fetchPreinforme]);

    const verInformePdf = async () => {
        setIsInformeError(false);
        setIsInformeLoad(true);
        const response = await getInformeByNumero(dispatch, preinforme.numeroInforme);
        const url = extractInformeUrl(response);
        if (url === null) {
            setIsInformeError(true);
            setInformeUrl(null);
        } else {
            setInformeUrl(url);
            window.open(url, '_blank', 'noopener,noreferrer');
        }
        setIsInformeLoad(false);
    };

    const goBack = () => {
        history.push(`/mediciones/user/${idBascula}`, {
            nameBascula: nameBascula,
            busquedaState: busqueda,
        });
    };

    // R3: CabeceraPreinforme (y su propio "Volver") solo se monta en el
    // estado cargado; en cualquier otro estado (cargando, error, o
    // preinforme null) el "Volver" vive al fondo de la página. Nunca
    // coinciden los dos: exactamente uno en todo momento.
    const hasCabecera = !isLoad && !isError && !!preinforme;

    const fechaMedicionValue = preinforme && preinforme.fechaMedicion
        ? UtilsDate().formatDDMMMYYYYHHmm(new Date(preinforme.fechaMedicion))
        : '--';

    // payloadData y tarjetasValor se derivan del preinforme cargado: no son
    // estado propio, se recalculan cuando cambia el preinforme (R19: con
    // payload ausente o no parseable, parsePreinformePayload ya degrada a
    // metricas: [] sin lanzar excepción).
    const payloadData = useMemo(
        () => parsePreinformePayload(preinforme && preinforme.payload),
        [preinforme]
    );
    const tarjetasValor = useMemo(
        () => buildTarjetasValor(preinforme, payloadData.metricas),
        [preinforme, payloadData]
    );
    const tarjetasMediciones = useMemo(
        () => buildTarjetasMediciones(preinforme && preinforme.mediciones),
        [preinforme]
    );

    // Secciones narrativas y aviso: derivados de payloadData, no son estado
    // propio (R4, R6, R7, R9, R11, R13).
    const oportunidades = useMemo(
        () => normalizarListaNarrativa(payloadData.oportunidades),
        [payloadData]
    );
    const fortalezas = useMemo(
        () => normalizarListaNarrativa(payloadData.fortalezas),
        [payloadData]
    );
    const recomendaciones = useMemo(
        () => normalizarListaNarrativa(payloadData.recomendaciones),
        [payloadData]
    );
    const metas = useMemo(
        () => resolveMetas12Semanas(payloadData.metas12Semanas),
        [payloadData]
    );
    const advertencias = useMemo(
        () => normalizarAdvertencias(payloadData.advertencias),
        [payloadData]
    );

    return (
        <>
            <Grid item xs={12}>
                <br />
                <br />
                <MigaPan
                    namePage="Preinforme"
                    to="/"
                    namePage2="Estaciones"
                    to2="/basculas/"
                    to3={{
                        pathname: `/mediciones/user/${idBascula}`,
                        state: { nameBascula: nameBascula, busquedaState: busqueda },
                    }}
                    namePage3={`Usuarios de Bascula ${nameBascula}`}
                >
                    Home
                </MigaPan>
            </Grid>
            <Grid item xs={12} md={12} lg={12}>
                <Title>Preinforme del usuario</Title>

                {isLoad && (
                    <div className={classes.loadingBox}>
                        <CircularProgress />
                        <Typography className={classes.loadingText}>
                            Cargando preinforme...
                        </Typography>
                    </div>
                )}

                {!isLoad && isError && (
                    <>
                        <Typography variant="body1" color="error">
                            Ocurrió un error al consultar el preinforme del usuario.
                        </Typography>
                        <Button
                            variant="contained"
                            color="primary"
                            style={{ marginTop: 16 }}
                            onClick={fetchPreinforme}
                        >
                            Reintentar
                        </Button>
                    </>
                )}

                {!isLoad && !isError && preinforme === null && (
                    <Typography variant="body1">
                        Este usuario no tiene preinforme generado
                    </Typography>
                )}

                {!isLoad && !isError && preinforme && (
                    <>
                        <CabeceraPreinforme
                            personalData={personalData}
                            preinforme={preinforme}
                            fechaMedicion={fechaMedicionValue}
                            onVolver={goBack}
                            onVerInformePdf={verInformePdf}
                            isInformeLoad={isInformeLoad}
                            informeUrl={informeUrl}
                        />

                        {isInformeError && (
                            <Typography variant="body1" color="error">
                                Ocurrió un error al consultar el informe en PDF.
                            </Typography>
                        )}

                        <Typography variant="h6" className={classesPre.sectionTitle}>
                            Parámetros biomédicos y signos vitales
                        </Typography>

                        {payloadData.metricas.length === 0 && (
                            <Typography className={classesPre.emptyMetricas}>
                                Sin detalle de rangos disponible
                            </Typography>
                        )}

                        <Grid container spacing={3}>
                            {payloadData.metricas.map((metrica) => (
                                <Grid item xs={12} md={6} lg={4} key={metrica.indicador}>
                                    <MetricaCard
                                        metrica={metrica}
                                        preinforme={preinforme}
                                    />
                                </Grid>
                            ))}
                            {tarjetasValor.map((tarjeta) => (
                                <Grid item xs={12} md={6} lg={4} key={tarjeta.clave}>
                                    <ValorCard
                                        etiqueta={tarjeta.etiqueta}
                                        valor={tarjeta.valor}
                                        unidad={tarjeta.unidad}
                                    />
                                </Grid>
                            ))}
                            {payloadData.ausentes.map((codigo) => (
                                <Grid item xs={12} md={6} lg={4} key={codigo}>
                                    <ValorCard etiqueta={labelIndicador(codigo)} atenuada />
                                </Grid>
                            ))}
                            {tarjetasMediciones.map((tarjeta) => (
                                <Grid item xs={12} md={6} lg={4} key={`medicion-${tarjeta.clave}`}>
                                    <ValorCard
                                        etiqueta={tarjeta.etiqueta}
                                        valor={tarjeta.valor}
                                        unidad={tarjeta.unidad}
                                        clasificacion={tarjeta.clasificacion}
                                        fecha={tarjeta.fecha}
                                        filas={tarjeta.filas}
                                        atenuada={tarjeta.atenuada}
                                    />
                                </Grid>
                            ))}
                        </Grid>

                        <AvisoAdvertencias advertencias={advertencias} />
                        <SeccionNarrativa
                            titulo="Oportunidades de mejora"
                            items={oportunidades}
                            conSeveridad
                            columnas={6}
                        />
                        <SeccionNarrativa
                            titulo="Fortalezas a conservar"
                            items={fortalezas}
                            conSeveridad
                            columnas={6}
                        />
                        <SeccionNarrativa
                            titulo="Recomendaciones para mejorar la salud"
                            items={recomendaciones}
                            numerada
                            columnas={4}
                        />
                        <MetasPanel metas={metas} />
                    </>
                )}

                {!hasCabecera && (
                    <Button
                        variant="contained"
                        color="primary"
                        className={classes.volverStandalone}
                        onClick={goBack}
                    >
                        Volver
                    </Button>
                )}
            </Grid>
        </>
    );
};

export default PreinformeUser;
